import { lazy, Suspense } from 'react';
import { ReactComponent as PlaneIcon } from '@public/images/ek/plane.svg';
import { colors } from '@constants/colors';
import { EventRole, IEvent } from '@interfaces/event';
import LoadingProgress from '@components/LoadingProgress';
import { env } from 'env';
import { IRoom } from '@interfaces/rooms';
import { IEventParticipant } from '@interfaces/eventParticipant';
import { IUser } from '@interfaces/user';
import { IXRHrBrandTypes } from '@clients/ek/interfaces';
import imageOfAvatar1EK from '@public/images/ek/avatars/1-FemaleRedPolo.png';
import imageOfAvatar2EK from '@public/images/ek/avatars/2-MaleRedPolo.png';
import imageOfAvatar3EK from '@public/images/ek/avatars/3-FemaleCCUniform.png';
import imageOfAvatar4EK from '@public/images/ek/avatars/4-FemaleBA.png';
import imageOfAvatar5EK from '@public/images/ek/avatars/5-FemalePurserUniform.png';
import imageOfAvatar6EK from '@public/images/ek/avatars/6-MalePurserUniform.png';
import imageOfAvatar7EK from '@public/images/ek/avatars/7-MaleCCUniform.png';
import imageOfAvatar8EK from '@public/images/ek/avatars/8-MaleBA.png';
import { convertUnixTimeToMilliSeconds, isScheduledEvent } from './events';

const FeedbackModal = lazy(
  () => import('@clients/ek/features/feedback/components/FeedbackModal'),
);

const {
  REACT_APP_CLIENT_ENV,
  REACT_APP_MULTI_PLAYER_TAG: REACT_APP_MULTI_PLAYER_TAG_FROM_ENV,
  REACT_APP_SD_EVENT_TAG,
  REACT_APP_FULL_TIME_SD_HUB_EVENT_TAG,
  REACT_APP_SEP_DISABLE_EVENT_BEFORE_START_TIME_MINS,
  REACT_APP_SEP_TRAINING_EVENT_TAG,
  REACT_APP_SEP_PRACTICE_EVENT_TAG,
  REACT_APP_SCHEDULED_EVENT_TAGS,
} = env;

const REACT_APP_MULTI_PLAYER_TAG =
  REACT_APP_MULTI_PLAYER_TAG_FROM_ENV || 'MultiPlayer';
const REACT_APP_SEP_EVENT_VISIBILITY_START_TIME_MINS = Number(
  REACT_APP_SEP_DISABLE_EVENT_BEFORE_START_TIME_MINS || 15,
);

export const getClientSpecificLastLoadingStepIcon = () => {
  let returnedIcon;
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK':
      returnedIcon = <PlaneIcon />;
      break;
    default:
      null;
  }

  return returnedIcon;
};

export const getClientSpecificNotificationTheme = () => {
  let returnedThemeData;
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK':
    default:
      returnedThemeData = {
        background: '#03030399',
        color: colors.white,
      };
      break;
  }

  return returnedThemeData;
};

export const getClientSpecificFullScreenHelperVisible = () => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK':
      return false;
    default:
      return true;
  }
};

export const getClientSpecificLoadingExperienceSpeed = () => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK':
      return 0.75;
    default:
      return 1;
  }
};

const sortSEPEvents = (events: IEvent[]) => {
  if (events.length <= 1) return events;

  const filteredEvents = [...events];

  const sepAssessmentIndex = filteredEvents.findIndex((x) =>
    x.eventTags?.includes('SEP Assessment'),
  );

  if (sepAssessmentIndex > -1) {
    const event = filteredEvents[sepAssessmentIndex];

    filteredEvents.splice(sepAssessmentIndex, 1);
    filteredEvents.unshift(event);

    return filteredEvents;
  }

  return events;
};

export const getClientSpecificSortedCarouselEvents = (events: IEvent[]) => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK':
      return sortSEPEvents(events);
    default:
      return events;
  }
};

export const getClientSpecificFirstTimeSDCarouselEvents = (
  events: (IEvent & { state: string | null | undefined })[],
  user: IUser,
): (IEvent & { state: string | null | undefined })[] => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      if (
        !events.length ||
        !REACT_APP_FULL_TIME_SD_HUB_EVENT_TAG ||
        !REACT_APP_SD_EVENT_TAG ||
        user.hrData.brand.toUpperCase() !== IXRHrBrandTypes.SD
      ) {
        return events;
      }

      const sdHubEvent = events.find((event) =>
        event.eventTags?.includes(REACT_APP_FULL_TIME_SD_HUB_EVENT_TAG),
      );

      if (!sdHubEvent) {
        return events;
      }

      if (sdHubEvent.state) {
        const state = JSON.parse(sdHubEvent.state);

        const isTutorialComplete = state?.bIsTutorialCompleted === true;

        if (isTutorialComplete) {
          return events;
        }
      }

      // Create protected tags set once, outside the function if possible
      const createProtectedTagsSet = () => {
        const scheduledTags =
          REACT_APP_SCHEDULED_EVENT_TAGS?.split(',').map((tag) => tag.trim()) ||
          [];

        return new Set(
          [
            ...scheduledTags,
            REACT_APP_FULL_TIME_SD_HUB_EVENT_TAG,
            REACT_APP_SEP_PRACTICE_EVENT_TAG,
          ].filter(Boolean),
        ); // Remove any undefined/null values
      };

      const protectedTags = createProtectedTagsSet();

      // Main processing function
      return events.map((event) => {
        const hasProtectedTag = event.eventTags?.some((tag) =>
          protectedTags.has(tag),
        );

        if (!hasProtectedTag) {
          event.isDisabled = true;
          event.isFirstTimeTooltip = true;
        }

        return event;
      });
    }
    default:
      return events;
  }
};

export const getClientSpecificSDTooltipTypeCarouselEvents = (
  events: IEvent[],
  user: IUser,
) => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      if (
        events.length &&
        user.hrData.brand.toUpperCase() === IXRHrBrandTypes.SD &&
        user.hrData.gradeCode
      ) {
        events.forEach((event) => {
          const isGradeCodeDisabled = event?.accessControlList?.find(
            (x) => x.path === 'gradeCode' && x.value === user.hrData.gradeCode,
          )?.disabled;

          if (isGradeCodeDisabled) {
            event.isNotAvailableForGradeCodeTooltip = true;
          }
        });
      }
      return events;
    }
    default:
      return events;
  }
};

export const getClientSpecificSEPVisibilityCarouselEvents = (
  events: (IEvent & { inviteStartTime: number })[],
  user: IUser,
) => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      if (
        events.length &&
        user.hrData.brand.toUpperCase() === IXRHrBrandTypes.SD
      ) {
        const now = Date.now();
        const minutesInMs =
          REACT_APP_SEP_EVENT_VISIBILITY_START_TIME_MINS * 60 * 1000;

        // Find training events that should be disabled
        const shouldDisableTraining = events.filter(
          (event) =>
            event.eventTags?.includes(
              REACT_APP_SEP_TRAINING_EVENT_TAG as string,
            ) &&
            now < event.inviteStartTime &&
            Math.abs(now - event.inviteStartTime) > minutesInMs,
        );

        if (shouldDisableTraining.length > 0) {
          // Disable training events
          shouldDisableTraining.forEach((event) => {
            event.isDisabled = true;
          });

          // Enable practice events (only if we disabled training events)
          events
            .filter((event) =>
              event.eventTags?.includes(
                REACT_APP_SEP_PRACTICE_EVENT_TAG as string,
              ),
            )
            .forEach((event) => {
              event.isDisabled = false;
            });
        }

        // disable scheduled events 15 mins before invite start time
        events
          .filter(
            (event) =>
              event.isScheduled &&
              now < convertUnixTimeToMilliSeconds(event.inviteStartTime) &&
              Math.abs(
                now - convertUnixTimeToMilliSeconds(event.inviteStartTime),
              ) > minutesInMs,
          )
          .forEach((event) => {
            event.isDisabled = true;
          });

        // disable scheduled events 1h after end time
        events
          .filter(
            (event) => event.isScheduled && now > (event.endTime + 3600) * 1000,
          )
          .forEach((event) => {
            event.isDisabled = true;
          });
      }

      return events;
    }
    default:
      return events;
  }
};

export const getClientSpecificSDCarouselEventsTransform = (
  events: (IEvent & {
    inviteStartTime: number;
    state: string | null | undefined;
  })[],
  user: IUser,
) => {
  getClientSpecificSEPVisibilityCarouselEvents(events, user);
  getClientSpecificSDTooltipTypeCarouselEvents(events, user);
  getClientSpecificFirstTimeSDCarouselEvents(events, user);
  getClientSpecificSortedCarouselEvents(events);

  return events;
};

export const getClientSpecificFeedbackModal = () => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK':
      return (
        <Suspense fallback={<LoadingProgress />}>
          <FeedbackModal />
        </Suspense>
      );
    default:
      return null;
  }
};

export const getClientSpecificCanShareWebCameraOnScreen = (
  currentRoom: IRoom | null,
  currentEvent: IEvent | undefined,
  currentEventParticipant: IEventParticipant | null,
) => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      const tags =
        currentEvent && Array.isArray(currentEvent.eventTags)
          ? currentEvent.eventTags
          : [];

      if (tags.includes(REACT_APP_MULTI_PLAYER_TAG)) {
        if (
          currentEventParticipant?.role === EventRole.Admin ||
          currentEventParticipant?.role === EventRole.Coordinator
        ) {
          return true;
        } else {
          return false;
        }
      }

      return !!currentRoom?.isMultiPlayer;
    }
    default:
      return !!currentRoom?.isMultiPlayer;
  }
};

export const getClientSpecificCanShareScreenOnScreen = (
  currentRoom: IRoom | null,
  currentEvent: IEvent | undefined,
  currentEventParticipant: IEventParticipant | null,
) => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      const tags =
        currentEvent && Array.isArray(currentEvent.eventTags)
          ? currentEvent.eventTags
          : [];

      if (tags.includes(REACT_APP_MULTI_PLAYER_TAG)) {
        if (
          currentEventParticipant?.role === EventRole.Admin ||
          currentEventParticipant?.role === EventRole.Coordinator
        ) {
          return true;
        } else {
          return false;
        }
      }

      return !!currentRoom?.isMultiPlayer;
    }
    default:
      return !!currentRoom?.isMultiPlayer;
  }
};

export const getClientSpecificCanShowWebCameraOnAvatar = (
  currentRoom: IRoom | null,
) => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return !!currentRoom?.isMultiPlayer;
    }
    default:
      return !!currentRoom?.isMultiPlayer;
  }
};

export const getClientSpecificCanSpeak = (currentRoom: IRoom | null) => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return !!currentRoom?.isMultiPlayer;
    }
    default:
      return !!currentRoom?.isMultiPlayer;
  }
};

export const getClientSpecificAvaturnCarousel = (): string[] => {
  const arrayOfAvaturnImages: string[] = [];
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK':
      {
        arrayOfAvaturnImages.push(imageOfAvatar1EK);
        arrayOfAvaturnImages.push(imageOfAvatar2EK);
        arrayOfAvaturnImages.push(imageOfAvatar3EK);
        arrayOfAvaturnImages.push(imageOfAvatar4EK);
        arrayOfAvaturnImages.push(imageOfAvatar5EK);
        arrayOfAvaturnImages.push(imageOfAvatar6EK);
        arrayOfAvaturnImages.push(imageOfAvatar7EK);
        arrayOfAvaturnImages.push(imageOfAvatar8EK);
      }
      return arrayOfAvaturnImages;
    default:
      return arrayOfAvaturnImages;
  }
};

export const getClientSpecificShowNotificationSuccessfulQrCodeScanning =
  (): boolean => {
    switch (REACT_APP_CLIENT_ENV) {
      case 'EK':
        return false;
      default:
        return true;
    }
  };

export const getClientSpecificIsLogoClickable = (): boolean => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK':
      return false;
    default:
      return true;
  }
};

export const getClientSpecificCanTeleport = (
  currentEvent: IEvent | undefined,
) => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      if (currentEvent && isScheduledEvent(currentEvent)) {
        return {
          canTeleport: false,
          notificationMessage: 'teleportUnavailable',
        };
      } else {
        return { canTeleport: true };
      }
    }
    default:
      return { canTeleport: true };
  }
};
